import styled, { css, keyframes } from 'styled-components';

import { utils } from '@desygner/ui-common-components';

import { PRESETS } from './ProjectCard.presets';

const appearFromTop = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-100%);
  }
  80% {
    opacity: 1;
    transform: translateY(0);
  }
  90% {
    transform: translateY(0px);
  }
  100% {
    transform: translateY(0);
  }
`;

export const StyledCard = styled.div<{ width?: number }>`
  ${({ theme, width }) => `
    display: flex;
    flex-direction: column;
    height: 100%;
    width: ${utils.rem(width ?? PRESETS.minWidth)};
    aspect-ratio: 1;

    @media (min-width: ${theme.Screen.Size.Medium}px) {
      width: ${utils.rem(width ?? PRESETS.maxwidth)};
    }
  `}
`;

export const StyledCardHeader = styled.div`
  ${({ theme }) =>
    `
      width: 100%;
      position: absolute;
      padding-block: ${utils.rem(theme.Spacing.Size.XSmall)};
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      align-self: stretch;
      z-index: ${theme.ZIndex.ZIndex.Elevated};
      & > div:first-child {
        padding-left: ${utils.rem(theme.Spacing.Size.XSmall)};
      };
    `}
`;

export const StyledCardHeaderRight = styled.div`
  ${({ theme }) =>
    `
      padding-right: ${utils.rem(theme.Spacing.Size.XSmall)};
      opacity: 0;
      display: flex;
      gap: ${utils.rem(theme.Spacing.Size.XXSmall)};
      margin: 0 0 0 auto;
    `}
`;

export const StyledCardHeaderButton = styled.div`
  ${({ theme }) =>
    `
      & button {
        background-color: ${theme.Color.NeutralsFixed.Gray};
      }
    `}
`;

export const StyledCardContent = styled.div`
  ${({ theme }) => {
    return css`
      display: flex;
      position: relative;
      height: 100%;
      border: ${utils.rem(theme.Border.Size.Small)} solid ${theme.Color.Neutrals.Border};
      border-radius: ${utils.rem(theme.Border.Radius.Medium)};

      &:hover .header-right-actions {
        opacity: 1;
        animation: ${appearFromTop} 0.5s ease forwards;
      }
    `;
  }}
`;

export const StyledCardFooter = styled.div`
  ${({ theme }) =>
    `
      display: flex;
      padding: ${utils.rem(theme.Spacing.Size.XSmall)} 0;
      align-items: center;
      gap: ${utils.rem(theme.Spacing.Size.XSmall)};
    `}
`;

export const StyledCardVideoWrapper = styled.video`
  ${({ theme }) => `
    border: ${utils.rem(theme.Border.Size.Small)} solid
      ${theme.Color.Neutrals.Border};
    max-width: 100%;
    max-height: 100%;
    border-radius: ${utils.rem(theme.Border.Radius.XSmall)};
  `}
`;

export const StyledCardImageWrapper = styled.img`
  ${({ theme }) => {
    return `
      border: ${utils.rem(theme.Border.Size.Small)} solid
        ${theme.Color.Neutrals.Border};
      max-width: 100%;
      max-height: 100%;
      border-radius: ${utils.rem(theme.Border.Radius.XSmall)};
  `;
  }}
`;

export const StyledCardSkeleton = styled.div`
  ${({ theme }) => `
    & .ant-skeleton-image {
      border-radius: ${utils.rem(theme.Border.Radius.Large)};
      width: ${utils.rem(PRESETS.maxwidth)} !important;
      height: ${utils.rem(PRESETS.maxwidth)} !important;
    }
  `}
`;

export const StyledViewer = styled.a<{ isLandscape: boolean }>`
  ${({ theme, isLandscape }) => `
    display: flex;
    justify-content: center;
    align-items: center;
    padding: ${utils.rem(theme.Spacing.Size.XSmall)};
    background-color: ${theme.Color.Neutrals.Light};
    border-radius: ${utils.rem(theme.Border.Radius.Medium)};
    
    ${
      isLandscape &&
      `
        height: ${utils.rem(PRESETS.minWidth)};

        @media (min-width: ${theme.Screen.Size.Medium}px) {
          height: ${utils.rem(PRESETS.maxwidth)};
        };
      `
    };
  
    & .swiper {
      height: 100%;
    }
  `}
`;

export const StyledCardSwiperVideoWrapper = styled.div`
  ${({ theme }) => `
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;

    & video {
      border: ${utils.rem(theme.Border.Size.Small)} solid
        ${theme.Color.Neutrals.Border}; 
      max-width: 100%;
      max-height: 100%;
      border-radius: ${utils.rem(theme.Border.Radius.XSmall)};
    }
  `}
`;

export const StyledCardSwiperImageWrapper = styled.div`
  ${({ theme }) => {
    return `

      display: flex;
      justify-content: center;
      height: 100%;

      & img {
        border: ${utils.rem(theme.Border.Size.Small)} solid
          ${theme.Color.Neutrals.Border};  
        max-width: 100%;
        max-height: 100%;
        border-radius: ${utils.rem(theme.Border.Radius.XSmall)};
      }
  `;
  }}
`;

export const StyledCardFooterTextWrapper = styled.div`
  ${({ theme }) =>
    `
      display: flex;
      flex-direction: column;
      gap: ${utils.rem(theme.Spacing.Size.XXSmall)};
      width: calc(100% - ${utils.rem(theme.Spacing.Size.Large)});
    `}
`;

export const StyledCardFooterIcon = styled.div`
  ${({ theme }) =>
    `
      display: flex;
      min-width: ${utils.rem(theme.Spacing.Size.MediumLarge)};
      min-height: ${utils.rem(theme.Spacing.Size.MediumLarge)};
      justify-content: center;
      align-items: center;
      background-color: ${theme.Color.Neutrals.Paper};
      border-radius: 50%;
    `}
`;
